import type React from "react";
import { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  type ColumnDef,
  flexRender,
} from "@tanstack/react-table";
import { useGet } from "../../hooks/useGet";
import { useFilterContext } from "../../context/FilterContextSub";
import CompaniesExportButton from "../components/dashboard/companies/CompaniesExportButton";
import CompaniesTablePagination from "../components/dashboard/companies/CompaniesTablePagination";
import type { CompaniesApiResponse, CompanyData } from "../../utils/types";

const Companies: React.FC = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const { appliedCompany, appliedStartDate, appliedEndDate } =
    useFilterContext();

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (appliedCompany) params.append("company", appliedCompany);
    if (appliedStartDate) params.append("startDate", appliedStartDate);
    if (appliedEndDate) params.append("endDate", appliedEndDate);
    params.append("page", (pageIndex + 1).toString());
    return params.toString();
  }, [appliedCompany, appliedStartDate, appliedEndDate, pageIndex]);

  const { data, isLoading, error } = useGet<CompaniesApiResponse>(
    `/users?${queryParams}`,
    ["companies_table", queryParams]
  );

  const columns = useMemo<ColumnDef<CompanyData>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Company Name",
        cell: (info) => (
          <div className="font-medium text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: (info) => (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-blue-600">
            {info.getValue() as string}
          </span>
        ),
      },
      {
        accessorKey: "keyword",
        header: "Keyword",
        cell: (info) => (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-blue-600">
            {info.getValue() as string}
          </span>
        ),
      },
      {
        accessorKey: "mt_responses_count",
        header: "MT Responses",
        cell: (info) => (
          <div className="text-gray-700">{info.getValue() as number}</div>
        ),
      },
      {
        accessorKey: "mo_count",
        header: "MO Count",
        cell: (info) => (
          <div className="text-gray-700">{info.getValue() as number}</div>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        cell: (info) => (
          <div className="text-xs text-gray-600">
            {new Date(info.getValue() as string).toLocaleString()}
          </div>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: data?.users.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: data?.users.last_page || 1,
    state: {
      pagination: {
        pageIndex,
        pageSize: 15,
      },
    },
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === "function"
          ? updater({ pageIndex, pageSize: 15 })
          : updater;
      setPageIndex(newState.pageIndex);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">Loading companies...</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-8">
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-4 sm:px-6 py-3.5 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No companies found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-full sm:max-w-7xl mx-auto">
      <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
        {/* Export Button */}
        <div className="p-4 border-b border-gray-100">
          <CompaniesExportButton data={data} />
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 sm:px-6 py-3.5 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No companies found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`transition-colors duration-150 ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    } hover:bg-gray-100`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-800"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <CompaniesTablePagination
          table={table}
          data={data}
          setPageIndex={setPageIndex}
        />
      </div>
    </div>
  );
};

export default Companies;
